const SLIDE_DIRECTION = {
  PREV: 0,
  NEXT: 1,
};
const MARGIN_RIGHT = 32; // 32px margin on the right for the last card

class Carousel {
  constructor(carouselElement) {
    this.carouselElement = carouselElement;
    this.carouselContainer = carouselElement.querySelector('.at-carousel__container');
    this.cardsContainer = carouselElement.querySelector('.at-carousel__cards-container');
    this.carouselItems = carouselElement.querySelectorAll('.at-carousel__card');
    this.prevButtonElement = this.carouselElement.querySelector('.at-carousel__button--prev');
    this.nextButtonElement = this.carouselElement.querySelector('.at-carousel__button--next');

    this.prevButtonElement.addEventListener('click', () => this.slideCard(SLIDE_DIRECTION.PREV));
    this.nextButtonElement.addEventListener('click', () => this.slideCard(SLIDE_DIRECTION.NEXT));

    this.isFirstCardFullyVisible = true;
    this.isLastCardFullyVisible = false;

    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.focusedCardIndex = 0;

    this.pageIsJustLoaded = true; // Prevents next button from being focused when the page initially loads

    this.updateButtonStates();
    this.addDragListeners();
    this.addEscapeKeyListener();

    this.carouselElement.addEventListener('focus', (event) => {
      this.lastFocusedElement = event.relatedTarget || document.activeElement;
      this.focusFirstCard();
    }, { once: true });

    this.addCardLinkArrowNavigation();
    this.addButtonShiftTabToFirstCard();
  }

  addButtonShiftTabToFirstCard() {
    this.prevButtonElement.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        this.focusFirstCard();
      }
    });

    this.nextButtonElement.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        this.focusFirstCard();
      }
    });
  }

  addDragListeners() {
    this.cardsContainer.addEventListener('mousedown', this.startDrag.bind(this));
    this.cardsContainer.addEventListener('touchstart', this.startDrag.bind(this), { passive: true });
    this.cardsContainer.addEventListener('mousemove', this.drag.bind(this));
    this.cardsContainer.addEventListener('touchmove', this.drag.bind(this), { passive: true });
    this.cardsContainer.addEventListener('mouseup', this.endDrag.bind(this));
    this.cardsContainer.addEventListener('mouseleave', this.endDrag.bind(this));
    this.cardsContainer.addEventListener('touchend', this.endDrag.bind(this));
  }

  startDrag(event) {
    this.isDragging = true;
    this.startX = event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
    this.prevTranslate = this.currentTranslate;
  }

  drag(event) {
    if (!this.isDragging) return;

    const currentX = event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
    const deltaX = currentX - this.startX;
    const potentialTranslate = this.prevTranslate + deltaX;

    const maxTranslate = 0;
    const minTranslate = -(this.cardsContainer.scrollWidth - this.carouselContainer.clientWidth) - MARGIN_RIGHT;

    this.currentTranslate = Math.max(minTranslate, Math.min(maxTranslate, potentialTranslate));
    this.cardsContainer.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  focusCardLink(direction) {
    const cardWidth = this.carouselItems[0].getBoundingClientRect().width;
    const cardGap = parseFloat(window.getComputedStyle(this.cardsContainer).gap);
    const totalCardWidth = cardWidth + cardGap;

    const currentIndex = Math.round(-this.currentTranslate / totalCardWidth);
    const targetIndex = direction === SLIDE_DIRECTION.NEXT
      ? Math.min(currentIndex + 1, this.carouselItems.length - 1)
      : Math.max(currentIndex - 1, 0);

    const targetCard = this.carouselItems[targetIndex];
    const link = targetCard.querySelector('a');
    if (link) {
      link.focus();
    }
  }

  endDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;

    const minTranslate = -(this.cardsContainer.scrollWidth - this.carouselContainer.clientWidth) - MARGIN_RIGHT;
    const maxTranslate = 0;

    this.currentTranslate = Math.max(minTranslate, Math.min(maxTranslate, this.currentTranslate));
    this.cardsContainer.style.transform = `translateX(${this.currentTranslate}px)`;

    this.isFirstCardFullyVisible = this.currentTranslate === maxTranslate;
    this.isLastCardFullyVisible = this.currentTranslate === minTranslate;
    this.updateButtonStates();
  }

  slideCard(direction) {
    const cardWidth = this.carouselItems[0].getBoundingClientRect().width;
    const cardGap = parseFloat(window.getComputedStyle(this.cardsContainer).gap);
    const totalCardWidth = cardWidth + cardGap;

    if (direction === SLIDE_DIRECTION.NEXT && !this.isLastCardFullyVisible) {
      this.currentTranslate -= totalCardWidth;
    } else if (direction === SLIDE_DIRECTION.PREV && !this.isFirstCardFullyVisible) {
      this.currentTranslate += totalCardWidth;
    }

    const minTranslate = -(this.cardsContainer.scrollWidth - this.carouselContainer.clientWidth) - MARGIN_RIGHT;
    const maxTranslate = 0;

    this.currentTranslate = Math.max(minTranslate, Math.min(maxTranslate, this.currentTranslate));
    this.cardsContainer.style.transform = `translateX(${this.currentTranslate}px)`;

    this.isFirstCardFullyVisible = this.currentTranslate === maxTranslate;
    this.isLastCardFullyVisible = this.currentTranslate === minTranslate;

    this.updateButtonStates();
  }

  updateButtonStates() {
    if (this.isFirstCardFullyVisible) {
      this.prevButtonElement.setAttribute('disabled', 'true');
      if (!this.pageIsJustLoaded) {
        this.nextButtonElement.focus();
      }
    } else {
      this.prevButtonElement.removeAttribute('disabled');
    }

    if (this.isLastCardFullyVisible) {
      this.nextButtonElement.setAttribute('disabled', 'true');
      this.prevButtonElement.focus();
    } else {
      this.nextButtonElement.removeAttribute('disabled');
    }

    this.updateButtonAriaLabels();

    this.pageIsJustLoaded = false;
  }

  addEscapeKeyListener() {
    this._boundEscapeHandler = (event) => {
      if (event.key !== 'Escape') return;

      const active = document.activeElement;

      if (active === this.prevButtonElement) {
        event.preventDefault();
        this.focusFirstCard();
        return;
      }

      if (active === this.nextButtonElement) {
        event.preventDefault();
        if (this.prevButtonElement.disabled === true) {
          this.focusFirstCard();
        } else {
          this.prevButtonElement.focus();
        }
        return;
      }

      if (this.carouselElement.contains(active)) {
        this.exitCarousel();
      }
    };
    document.addEventListener('keydown', this._boundEscapeHandler);
  }

  exitCarousel() {
    this.carouselElement.setAttribute('tabindex', '-1');
    this.carouselElement.blur();
    document.activeElement.blur();
    this.lastFocusedElement.focus();
  }

  focusFirstCard() {
    this.currentTranslate = 0;
    this.cardsContainer.style.transform = 'translateX(0px)';
    this.isFirstCardFullyVisible = true;
    this.isLastCardFullyVisible = false;
    this.updateButtonStates();

    this.setCardTabIndices(0);

    const firstCard = this.carouselItems[0];
    if (firstCard) {
      const link = firstCard.querySelector('a');
      if (link) {
        link.focus();
      }
    }
  }

  updateButtonAriaLabels() {
    if (this.isFirstCardFullyVisible) {
      this.prevButtonElement.setAttribute('aria-label', 'Back');
    } else {
      this.prevButtonElement.setAttribute('aria-label', 'Last slide (left arrow key)');
    }

    if (this.isLastCardFullyVisible) {
      this.nextButtonElement.setAttribute('aria-label', 'Next');
    } else {
      this.nextButtonElement.setAttribute('aria-label', 'Next slide (right arrow key)');
    }
  }

  addCardLinkArrowNavigation() {
    this.carouselItems.forEach((card, index) => {
      const link = card.querySelector('a');
      if (link) {
        link.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            if (index < this.carouselItems.length - 1) {
              this.slideToCard(index + 1);
              this.focusCardLinkByIndex(index + 1);
            }
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            if (index > 0) {
              this.slideToCard(index - 1);
              this.focusCardLinkByIndex(index - 1);
            }
          } else if (event.key === 'Tab' && !event.shiftKey) {
            if (link.tabIndex === 0) {
              event.preventDefault();
              if (this.prevButtonElement.disabled === true) {
                this.nextButtonElement.focus();
              } else {
                this.prevButtonElement.focus();
              }
            }
          }
        });
      }
    });
  }

  slideToCard(index) {
    const cardWidth = this.carouselItems[0].getBoundingClientRect().width;
    const cardGap = parseFloat(window.getComputedStyle(this.cardsContainer).gap);
    const totalCardWidth = cardWidth + cardGap;
    this.currentTranslate = -index * totalCardWidth;
    const minTranslate = -(this.cardsContainer.scrollWidth - this.carouselContainer.clientWidth) - MARGIN_RIGHT;
    const maxTranslate = 0;
    this.currentTranslate = Math.max(minTranslate, Math.min(maxTranslate, this.currentTranslate));
    this.cardsContainer.style.transform = `translateX(${this.currentTranslate}px)`;
    this.isFirstCardFullyVisible = this.currentTranslate === maxTranslate;
    this.isLastCardFullyVisible = this.currentTranslate === minTranslate;
    this.updateButtonStates();
  }

  focusCardLinkByIndex(index) {
    this.setCardTabIndices(index);
    const card = this.carouselItems[index];
    if (card) {
      const link = card.querySelector('a');
      if (link) {
        link.focus();
        this.focusedCardIndex = index;
      }
    }
  }

  setCardTabIndices(activeIndex) {
    this.carouselItems.forEach((card, idx) => {
      const link = card.querySelector('a');
      if (link) {
        link.tabIndex = (idx === activeIndex) ? 0 : -1;
      }
    });
  }
}

const initializeCarousel = () => {
  document.querySelectorAll('[data-element="at-carousel"]').forEach((carousel) => new Carousel(carousel));
};

export {
  Carousel,
  initializeCarousel,
};